// src/pages/finance-portal/bank-reconciliation/ReconciliationWorksheet.js
//
// Reconciliation worksheet (matching UI).
// Shows matched lines plus side-by-side bank-only / GL-only candidate
// columns. User can manually match, unmatch, run auto-match, complete &
// archive. Exception list rendered inline.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid,
    IconButton, MenuItem, Skeleton, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckIcon from '@mui/icons-material/Check';
import ArchiveIcon from '@mui/icons-material/Archive';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';

const STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#475569' },
    IN_REVIEW: { bg: '#fef3c7', color: '#92400e' },
    EXCEPTION: { bg: '#fee2e2', color: '#991b1b' },
    RECONCILED: { bg: '#dcfce7', color: '#166534' },
    CLOSED: { bg: '#dbeafe', color: '#1e40af' },
};

const SEVERITY_COLORS = {
    LOW: '#10b981', MEDIUM: '#f59e0b', HIGH: '#ef4444',
};

const EXCEPTION_LABELS = {
    TIMING_DIFFERENCE: 'Timing Difference',
    MISSING_GL_ENTRY: 'Missing GL Entry',
    MISSING_BANK_TXN: 'Missing Bank Txn',
    AMOUNT_DISCREPANCY: 'Amount Discrepancy',
    DUPLICATE: 'Duplicate',
    FX_VARIANCE: 'FX Variance',
};

function formatCurrency(amount) {
    if (amount == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export default function ReconciliationWorksheet() {
    const navigate = useNavigate();
    const { reconciliationId } = useParams();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isMatching, setIsMatching] = useState(false);
    const [error, setError] = useState(null);
    const [selectedBankId, setSelectedBankId] = useState(null);
    const [selectedGlLineId, setSelectedGlLineId] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [resolveDialog, setResolveDialog] = useState({ open: false, exception: null, action: 'OTHER', notes: '' });
    const [completeDialog, setCompleteDialog] = useState({ open: false, allowVariance: false });

    const fetchData = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/reconciliations/${reconciliationId}`);
            if (res.data?.success) setData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load reconciliation.');
        } finally {
            setIsLoading(false);
        }
    }, [reconciliationId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (!selectedBankId) { setSuggestions([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get(`/api/finance/reconciliations/${reconciliationId}/suggest-matches`, {
                    params: { stripe_transaction_id: selectedBankId },
                });
                if (!cancelled && res.data?.success) setSuggestions(res.data.data || []);
            } catch (err) {
                if (!cancelled) setSuggestions([]);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedBankId, reconciliationId]);

    const handleAutoMatch = async () => {
        setIsMatching(true);
        try {
            await axiosInstance.post(`/api/finance/reconciliations/${reconciliationId}/auto-match`);
            await fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Auto-match failed.');
        } finally {
            setIsMatching(false);
        }
    };

    const handleManualMatch = async (txnId, lineId) => {
        try {
            await axiosInstance.post(`/api/finance/reconciliations/${reconciliationId}/match`, {
                stripe_transaction_id: txnId,
                journal_line_id: lineId,
            });
            setSelectedBankId(null);
            setSelectedGlLineId(null);
            setSuggestions([]);
            await fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Match failed.');
        }
    };

    const handleUnmatch = async (lineId) => {
        try {
            await axiosInstance.post(`/api/finance/reconciliations/${reconciliationId}/lines/${lineId}/unmatch`);
            await fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Unmatch failed.');
        }
    };

    const handleResolveException = async () => {
        try {
            await axiosInstance.post(
                `/api/finance/reconciliations/${reconciliationId}/exceptions/${resolveDialog.exception.id}/resolve`,
                { resolution_action: resolveDialog.action, resolution_notes: resolveDialog.notes },
            );
            setResolveDialog({ open: false, exception: null, action: 'OTHER', notes: '' });
            await fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Resolve failed.');
        }
    };

    const handleComplete = async () => {
        try {
            await axiosInstance.post(`/api/finance/reconciliations/${reconciliationId}/complete`, {
                allow_with_variance: completeDialog.allowVariance,
            });
            setCompleteDialog({ open: false, allowVariance: false });
            await fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Complete failed.');
        }
    };

    const handleArchive = async () => {
        try {
            await axiosInstance.post(`/api/finance/reconciliations/${reconciliationId}/archive`);
            await fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Archive failed.');
        }
    };

    const rec = data?.reconciliation;
    const matched = data?.matched_lines || [];
    const bankOnly = data?.bank_only_lines || [];
    const glOnly = data?.gl_only_lines || [];
    const exceptions = data?.exceptions || [];

    const isClosed = rec && (rec.status === 'CLOSED');
    const isReconciled = rec && (rec.status === 'RECONCILED' || rec.status === 'CLOSED');

    const variance = useMemo(() => Number(rec?.variance || 0), [rec]);

    if (isLoading) {
        return (
            <Box sx={{ p: 3, fontFamily, bgcolor: '#f8fafc', minHeight: '100vh' }}>
                <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={400} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton onClick={() => navigate('/finance-portal/bank-reconciliation')} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.4rem', color: '#0f172a' }}>
                                {rec?.account_name || `Reconciliation #${rec?.id}`}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                {rec?.institution_name} • {rec?.statement_period_start} → {rec?.statement_period_end}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                        label={rec?.status}
                        sx={{
                            fontFamily, fontWeight: 700,
                            bgcolor: STATUS_COLORS[rec?.status]?.bg,
                            color: STATUS_COLORS[rec?.status]?.color,
                        }}
                    />
                    {!isClosed && !isReconciled && (
                        <Button
                            variant="outlined"
                            startIcon={isMatching ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
                            onClick={handleAutoMatch}
                            disabled={isMatching}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}
                        >
                            Auto-Match
                        </Button>
                    )}
                    {!isReconciled && (
                        <Button
                            variant="contained"
                            startIcon={<CheckIcon />}
                            onClick={() => setCompleteDialog({ open: true, allowVariance: false })}
                            disabled={Math.abs(variance) > 0.01 && exceptions.some(e => e.status === 'OPEN' || e.status === 'INVESTIGATING')}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT }}
                        >
                            Complete
                        </Button>
                    )}
                    {rec?.status === 'RECONCILED' && (
                        <Button
                            variant="contained"
                            startIcon={<ArchiveIcon />}
                            onClick={handleArchive}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: '#10b981' }}
                        >
                            Archive
                        </Button>
                    )}
                    <IconButton onClick={fetchData}><RefreshIcon /></IconButton>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Balance roll */}
            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 1 }}>
                                Bank Side
                            </Typography>
                            <Stack spacing={0.75} sx={{ fontFamily, fontSize: '0.85rem' }}>
                                <Stack direction="row" justifyContent="space-between"><span>Statement Ending Balance</span><b>{formatCurrency(rec?.statement_ending_balance)}</b></Stack>
                                <Stack direction="row" justifyContent="space-between"><span>+ Deposits in Transit</span><b>{formatCurrency(rec?.deposits_in_transit_total)}</b></Stack>
                                <Stack direction="row" justifyContent="space-between"><span>− Outstanding Checks</span><b>{formatCurrency(rec?.outstanding_checks_total)}</b></Stack>
                                <Divider />
                                <Stack direction="row" justifyContent="space-between"><span style={{ fontWeight: 700 }}>= Adjusted Bank Balance</span><b>{formatCurrency(rec?.adjusted_bank_balance)}</b></Stack>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 1 }}>
                                Book Side
                            </Typography>
                            <Stack spacing={0.75} sx={{ fontFamily, fontSize: '0.85rem' }}>
                                <Stack direction="row" justifyContent="space-between"><span>GL Ending Balance</span><b>{formatCurrency(rec?.gl_ending_balance)}</b></Stack>
                                <Stack direction="row" justifyContent="space-between"><span>+ Bank Interest</span><b>{formatCurrency(rec?.bank_interest_total)}</b></Stack>
                                <Stack direction="row" justifyContent="space-between"><span>− Bank Charges</span><b>{formatCurrency(rec?.bank_charges_total)}</b></Stack>
                                <Stack direction="row" justifyContent="space-between"><span>± Other Adjustments</span><b>{formatCurrency(rec?.other_adjustments_total)}</b></Stack>
                                <Divider />
                                <Stack direction="row" justifyContent="space-between"><span style={{ fontWeight: 700 }}>= Adjusted Book Balance</span><b>{formatCurrency(rec?.adjusted_book_balance)}</b></Stack>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#475569' }}>
                            Variance:
                        </Typography>
                        <Typography sx={{
                            fontFamily, fontWeight: 800, fontSize: '1.5rem',
                            color: Math.abs(variance) < 0.01 ? '#10b981' : '#ef4444',
                        }}>
                            {formatCurrency(variance)}
                        </Typography>
                        {Math.abs(variance) < 0.01 ? (
                            <Chip icon={<CheckIcon />} label="Balanced" sx={{ fontFamily, fontWeight: 700, bgcolor: '#dcfce7', color: '#166534' }} />
                        ) : (
                            <Chip icon={<WarningAmberIcon />} label="Out of Balance" sx={{ fontFamily, fontWeight: 700, bgcolor: '#fee2e2', color: '#991b1b' }} />
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 0 }}>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        sx={{ borderBottom: '1px solid #e2e8f0', '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600 } }}
                    >
                        <Tab label={`Worksheet (${bankOnly.length} bank • ${glOnly.length} GL)`} />
                        <Tab label={`Matched (${matched.length})`} />
                        <Tab label={`Exceptions (${exceptions.filter(e => e.status === 'OPEN' || e.status === 'INVESTIGATING').length})`} />
                    </Tabs>

                    {/* Worksheet */}
                    {tab === 0 && (
                        <Box sx={{ p: 2.5 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>
                                        Bank Transactions ({bankOnly.length})
                                    </Typography>
                                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', maxHeight: 500, overflowY: 'auto' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, bgcolor: '#f8fafc' } }}>
                                                    <TableCell padding="checkbox" />
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {bankOnly.length === 0 && (
                                                    <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', fontFamily, color: '#94a3b8', py: 3 }}>All matched.</TableCell></TableRow>
                                                )}
                                                {bankOnly.map((b) => (
                                                    <TableRow
                                                        key={b.id}
                                                        hover
                                                        selected={selectedBankId === b.stripe_transaction_id}
                                                        onClick={() => setSelectedBankId(b.stripe_transaction_id)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox checked={selectedBankId === b.stripe_transaction_id} size="small" />
                                                        </TableCell>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{b.bank_date}</TableCell>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{b.bank_description || '—'}</TableCell>
                                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(b.bank_amount)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', mb: 1 }}>
                                        GL Entries ({glOnly.length})
                                        {selectedBankId && suggestions.length > 0 && (
                                            <Chip
                                                label={`${suggestions.length} suggestion${suggestions.length === 1 ? '' : 's'}`}
                                                size="small"
                                                sx={{ ml: 1, fontFamily, fontSize: '0.7rem', bgcolor: '#dbeafe', color: '#1e40af' }}
                                            />
                                        )}
                                    </Typography>
                                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', maxHeight: 500, overflowY: 'auto' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, bgcolor: '#f8fafc' } }}>
                                                    <TableCell padding="checkbox" />
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {glOnly.length === 0 && (
                                                    <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', fontFamily, color: '#94a3b8', py: 3 }}>All matched.</TableCell></TableRow>
                                                )}
                                                {glOnly
                                                    .slice()
                                                    .sort((a, b) => {
                                                        const sa = suggestions.find(s => s.journal_line_id === a.journal_line_id);
                                                        const sb = suggestions.find(s => s.journal_line_id === b.journal_line_id);
                                                        return (sb?.confidence || 0) - (sa?.confidence || 0);
                                                    })
                                                    .map((g) => {
                                                        const suggestion = suggestions.find(s => s.journal_line_id === g.journal_line_id);
                                                        return (
                                                            <TableRow
                                                                key={g.id}
                                                                hover
                                                                selected={selectedGlLineId === g.journal_line_id}
                                                                onClick={() => setSelectedGlLineId(g.journal_line_id)}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    bgcolor: suggestion ? 'rgba(59, 130, 246, 0.06)' : 'inherit',
                                                                }}
                                                            >
                                                                <TableCell padding="checkbox">
                                                                    <Checkbox checked={selectedGlLineId === g.journal_line_id} size="small" />
                                                                </TableCell>
                                                                <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{g.gl_date}</TableCell>
                                                                <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>
                                                                    {g.gl_description || '—'}
                                                                    {suggestion && (
                                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: ACCENT, fontWeight: 700 }}>
                                                                            {(suggestion.confidence * 100).toFixed(0)}% match
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(g.gl_amount)}</TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<LinkIcon />}
                                    onClick={() => handleManualMatch(selectedBankId, selectedGlLineId)}
                                    disabled={!selectedBankId || !selectedGlLineId || isReconciled}
                                    sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT }}
                                >
                                    Match Selected Pair
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Matched */}
                    {tab === 1 && (
                        <Box sx={{ p: 0 }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, bgcolor: '#f8fafc' } }}>
                                        <TableCell>Bank Date</TableCell>
                                        <TableCell>Bank Description</TableCell>
                                        <TableCell align="right">Bank Amount</TableCell>
                                        <TableCell>GL Date</TableCell>
                                        <TableCell>GL Description</TableCell>
                                        <TableCell align="right">GL Amount</TableCell>
                                        <TableCell>Confidence</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {matched.length === 0 && (
                                        <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, fontFamily, color: '#94a3b8' }}>No matches yet.</TableCell></TableRow>
                                    )}
                                    {matched.map((m) => (
                                        <TableRow key={m.id} hover>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{m.bank_date}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{m.bank_description || '—'}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(m.bank_amount)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{m.gl_date}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{m.gl_description || '—'}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>{formatCurrency(m.gl_amount)}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>
                                                {m.match_confidence != null ? `${(m.match_confidence * 100).toFixed(0)}%` : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={m.match_type} size="small" sx={{ fontFamily, fontSize: '0.7rem' }} />
                                            </TableCell>
                                            <TableCell align="right">
                                                {!isReconciled && (
                                                    <Tooltip title="Unmatch">
                                                        <IconButton size="small" onClick={() => handleUnmatch(m.id)}>
                                                            <LinkOffIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {/* Exceptions */}
                    {tab === 2 && (
                        <Box sx={{ p: 0 }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, bgcolor: '#f8fafc' } }}>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Severity</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell align="right">Variance</TableCell>
                                        <TableCell>SLA Due</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {exceptions.length === 0 && (
                                        <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, fontFamily, color: '#94a3b8' }}>No exceptions.</TableCell></TableRow>
                                    )}
                                    {exceptions.map((e) => (
                                        <TableRow key={e.id} hover>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>
                                                {EXCEPTION_LABELS[e.exception_type] || e.exception_type}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={e.severity}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                                        bgcolor: `${SEVERITY_COLORS[e.severity]}20`,
                                                        color: SEVERITY_COLORS[e.severity],
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>{e.description}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily }}>
                                                {e.variance_amount != null ? formatCurrency(e.variance_amount) : '—'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{e.sla_due_date || '—'}</TableCell>
                                            <TableCell>
                                                <Chip label={e.status} size="small" sx={{ fontFamily, fontSize: '0.7rem' }} />
                                            </TableCell>
                                            <TableCell align="right">
                                                {(e.status === 'OPEN' || e.status === 'INVESTIGATING') && !isReconciled && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => setResolveDialog({ open: true, exception: e, action: 'OTHER', notes: '' })}
                                                        sx={{ fontFamily, textTransform: 'none' }}
                                                    >
                                                        Resolve
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Resolve dialog */}
            <Dialog open={resolveDialog.open} onClose={() => setResolveDialog({ open: false, exception: null, action: 'OTHER', notes: '' })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Resolve Exception</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569' }}>
                            {resolveDialog.exception?.description}
                        </Typography>
                        <TextField
                            select
                            label="Action Taken"
                            value={resolveDialog.action}
                            onChange={(e) => setResolveDialog({ ...resolveDialog, action: e.target.value })}
                            fullWidth
                        >
                            <MenuItem value="CREATED_GL_ENTRY">Created GL entry</MenuItem>
                            <MenuItem value="ADJUSTED_BANK">Adjusted bank entry</MenuItem>
                            <MenuItem value="WAIVED">Waived (acceptable variance)</MenuItem>
                            <MenuItem value="REASSIGNED">Reassigned for review</MenuItem>
                            <MenuItem value="OTHER">Other</MenuItem>
                        </TextField>
                        <TextField
                            label="Resolution Notes"
                            value={resolveDialog.notes}
                            onChange={(e) => setResolveDialog({ ...resolveDialog, notes: e.target.value })}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolveDialog({ open: false, exception: null, action: 'OTHER', notes: '' })} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleResolveException} sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT }}>
                        Mark Resolved
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Complete dialog */}
            <Dialog open={completeDialog.open} onClose={() => setCompleteDialog({ open: false, allowVariance: false })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Complete Reconciliation</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, mb: 2 }}>
                        Variance: <b>{formatCurrency(variance)}</b>
                    </Typography>
                    {Math.abs(variance) > 0.01 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            The reconciliation has a non-zero variance. Completing requires explicit override.
                        </Alert>
                    )}
                    <FormControlLabelInline
                        checked={completeDialog.allowVariance}
                        onChange={(checked) => setCompleteDialog({ ...completeDialog, allowVariance: checked })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompleteDialog({ open: false, allowVariance: false })} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleComplete}
                        disabled={Math.abs(variance) > 0.01 && !completeDialog.allowVariance}
                        sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT }}
                    >
                        Confirm Complete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function FormControlLabelInline({ checked, onChange }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', fontFamily }}>
            <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />
            <Typography sx={{ fontFamily, fontSize: '0.9rem' }}>
                Allow completion despite non-zero variance (audit-logged override)
            </Typography>
        </Box>
    );
}
